import { supabase, callFn } from './supabase'
import type { Consignor, Customer, Employee, IntakeDraft, IntakeResult, Piece, SoldInfo, SaleDraft, SaleRow, SalePortion, PaymentHandles, TaxSettings, TradeDraft, TradeRow, TradeLimits, PendingIntake } from './types'
import { lineCents, giveCents, TAX_DEFAULT, taxOptional } from './types'
import { splitPct } from './types'
import { INTAKE_CARD_COLS, type IntakeRow } from './intakeCard'

export async function loadEmployees(): Promise<Employee[]> {
  const { data, error } = await supabase.from('employees').select('*').order('employee_id')
  if (error) throw error
  return data as Employee[]
}

export async function loadConsignors(): Promise<Consignor[]> {
  const [{ data, error }, { data: pieces }] = await Promise.all([
    supabase.from('consignors').select('*').order('first_name'),
    supabase.from('v_consignor_pieces').select('*'),
  ])
  if (error) throw error
  const count = new Map((pieces ?? []).map((p: { consignor_id: string; pieces: number }) => [p.consignor_id, p.pieces]))
  return (data as Consignor[]).map((c) => ({ ...c, pieces: count.get(c.id) ?? 0 }))
}

export async function loadCustomers(): Promise<Customer[]> {
  const [{ data, error }, { data: pieces }] = await Promise.all([
    supabase.from('customers').select('*').order('first_name'),
    supabase.from('v_customer_pieces').select('*'),
  ])
  if (error) throw error
  const count = new Map((pieces ?? []).map((p: { customer_id: string; pieces: number }) => [p.customer_id, p.pieces]))
  return (data as Customer[]).map((c) => ({ ...c, pieces: count.get(c.id) ?? 0 }))
}

export async function loadBrands(): Promise<string[]> {
  const { data, error } = await supabase.from('brands').select('name').order('name')
  if (error) throw error
  return (data as { name: string }[]).map((b) => b.name)
}

export async function addBrand(name: string, employeeId: string | null) {
  const { error } = await supabase.from('brands').insert({ name: name.trim(), created_by: employeeId })
  if (error && !error.message.includes('duplicate')) throw error
}

export function createCustomer(input: Pick<Customer, 'first_name' | 'last_name' | 'phone' | 'email' | 'notes'>, employeeId: string | null) {
  return callFn<Customer & { shopify_error?: string | null }>('customer-create', { ...input, employee_id: employeeId })
}

export function updateCustomer(input: Pick<Customer, 'id' | 'first_name' | 'last_name' | 'phone' | 'email' | 'notes'>, employeeId: string | null) {
  return callFn<Customer & { shopify_error?: string | null }>('customer-update', { ...input, employee_id: employeeId })
}

export interface ShopifyHit { shopify_customer_id: string; local_id: string | null; first_name: string; last_name: string; email: string; phone: string }
export function searchShopifyCustomers(q: string) {
  return callFn<{ shopify: ShopifyHit[]; error: string | null }>('customer-search', { q })
}
export function linkShopifyCustomer(shopifyCustomerId: string, employeeId: string | null) {
  return callFn<Customer>('customer-search', { action: 'link', shopify_customer_id: shopifyCustomerId, employee_id: employeeId })
}

export async function nextIntakeNo(): Promise<number> {
  // A failed read throws (16 Oct: it used to be swallowed, and a wifi drop left the form on #0000 until a relaunch).
  const { data, error } = await supabase.from('counters').select('value').eq('name', 'intake_no').maybeSingle()
  if (error) throw new Error('Could not read the next intake number: ' + error.message)
  return ((data?.value as number | undefined) ?? 0) + 1
}

/** The intake number as shown on screen: four digits, or a dash while it has not loaded. */
export const intakeNoLabel = (n: number) => (n > 0 ? String(n).padStart(4, '0') : '----')

export function createConsignor(input: Omit<Consignor, 'id' | 'key' | 'consignify_id' | 'in_consignify' | 'pieces'>, employeeId: string | null) {
  return callFn<Consignor>('consignor-create', { ...input, employee_id: employeeId })
}

export function updateConsignor(id: string, input: Partial<Omit<Consignor, 'id' | 'key' | 'consignify_id' | 'in_consignify' | 'pieces'>>, employeeId: string | null) {
  return callFn<Consignor>('consignor-update', { ...input, id, employee_id: employeeId })
}

export function createIntake(d: IntakeDraft, employeeId: string) {
  const consigned = d.type === 'consigned'
  const sourced = !consigned && !d.pendingId && d.sourced
  return callFn<IntakeResult>('intake-create', {
    employee_id: employeeId,
    type: d.pendingId ? 'traded' : d.type,
    pending_intake_id: d.pendingId || undefined,
    counterparty_name: consigned ? '' : sourced ? 'Shane Sears' : d.from.trim(),
    customer_id: consigned || sourced ? null : (d.customerId || null),
    sourced_by: sourced ? 'shane' : null, sourced_from: sourced ? d.source.trim() : '',
    consignor_id: consigned ? d.consignorId : null,
    cost_cents: consigned ? 0 : Math.round(Number(d.cost || 0) * 100),
    split_pct: consigned ? splitPct(d) : null,
    consignor_minimum_cents: consigned && Number(d.min) > 0 ? Math.round(Number(d.min) * 100) : null,   // optional: blank = no floor
    payout_method: d.pendingId ? 'trade_credit' : d.payout,
    whose_piece: consigned ? 'consignor' : d.whose,
    brand: d.brand.trim(), category_id: d.category, category: d.categoryName, item: d.item.trim(), size: d.size.trim(), measurements: d.measurements.trim(), listing: d.listing, condition: d.cond, deal_note: d.note.trim(), authenticated: d.authenticated,
  })
}

export async function jobStatus(intakeId: string) {
  const { data } = await supabase.from('intakes').select('consignify_status, shopify_product_id').eq('id', intakeId).maybeSingle()
  return data as { consignify_status: string; shopify_product_id: string | null } | null
}

// ---- selling
export function lookupPiece(code: string) {
  return callFn<{ piece: Piece; sold: SoldInfo | null; out_of_stock?: boolean; open_sale: { sale_no: number; status: string; at: string; by: string } | null; same_tag?: string[] }>('product-lookup', { code })
}

export function browsePieces(cursor: string | null) {
  return callFn<{ pieces: (Piece & { sold_here: boolean; open_sale?: boolean })[]; cursor: string | null }>('product-search', { browse: true, cursor })
}

export function searchPieces(q: string) {
  return callFn<{ pieces: (Piece & { sold_here: boolean; open_sale?: boolean })[] }>('product-search', { q })
}

export function createSale(d: SaleDraft, employeeId: string, approvedBy: string | null, cardCents = 0) {
  return callFn<{ sale_id: string; sale_no: number; subtotal_cents: number; tax_cents: number; total_cents: number; tax_included_cents: number; status: 'awaiting_payment' | 'awaiting_card' }>('sale-create', {
    employee_id: employeeId, approved_by: approvedBy, payment_method: d.method, tax: taxOptional(d) && d.taxMode === 'none' ? 'none' : 'add',
    payments: d.method === 'split' ? d.parts.map((p) => ({ method: p.method, amount_cents: p.method === 'card' ? cardCents : Math.round(Number(p.amount || 0) * 100), crypto_coin: p.method === 'crypto' ? p.coin : undefined, crypto_network: p.method === 'crypto' ? p.network : undefined })) : undefined,
    zelle_sender: d.zelleSender, crypto_coin: d.coin, crypto_network: d.network, crypto_tx: d.cryptoTx, customer_id: d.customerId || null, customer_name: d.customerName,
    lines: d.lines.map((l) => ({ ...l.piece, price_cents: lineCents(l), entered_cost_cents: l.cost ? Math.round(Number(l.cost) * 100) : null, entered_owner: l.owner || null })),
  })
}

export function confirmPayment(saleId: string, employeeId: string, extra: { payment_id?: string; zelle_sender?: string; crypto_tx?: string; crypto_coin?: string; crypto_network?: string; paypal_ref?: string }) {
  return callFn<{ ok: boolean; paid: boolean; stock: Record<string, string>; remaining?: { id: string; method: string; amount_cents: number }[] }>('sale-confirm-payment', { sale_id: saleId, employee_id: employeeId, ...extra })
}

export async function loadSale(saleId: string) {
  const { data, error } = await supabase.from('sales').select('*, lines:sale_lines(*), payments:sale_payments(*)').eq('id', saleId).maybeSingle()
  if (error) throw error
  if (data?.payments) (data.payments as SalePortion[]).sort((a, b) => a.position - b.position)
  return data as (SaleRow & { zelle_sender: string; crypto_coin: string | null; crypto_network: string | null; crypto_tx: string; confirmed_by: string | null; subtotal_cents: number; tax_cents: number; payments: SalePortion[]; lines: { title: string; sku: string; price_cents: number; below_floor: boolean; list_price_cents: number }[] }) | null
}

export async function loadPaymentHandles(): Promise<PaymentHandles> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'payment_handles').maybeSingle()
  const v = (data?.value ?? {}) as Partial<PaymentHandles>
  return { zelle_name: v.zelle_name ?? '', zelle_qr_url: v.zelle_qr_url ?? '', paypal_me: v.paypal_me ?? '', paypal_qr_url: v.paypal_qr_url ?? '', counter_tender_name: v.counter_tender_name || 'Paid at counter', wallets: v.wallets ?? {} }
}
export async function loadTaxSettings(): Promise<TaxSettings> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'tax').maybeSingle()
  const v = (data?.value ?? {}) as Partial<TaxSettings>
  const rate = Number(v.rate); const ex = Number(v.clothing_exempt_under_cents)
  return { rate: Number.isFinite(rate) && rate >= 0 && rate < 1 ? rate : TAX_DEFAULT.rate, clothing_exempt_under_cents: Number.isFinite(ex) && ex >= 0 ? ex : 0 }
}

export async function loadMode(): Promise<{ rehearsal: boolean; label: string }> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'mode').maybeSingle()
  return (data?.value as { rehearsal: boolean; label: string }) ?? { rehearsal: false, label: '' }
}

export function updatePaymentHandles(value: PaymentHandles, ownerId: string) {
  return callFn<{ ok: boolean }>('settings-update', { key: 'payment_handles', value, owner_id: ownerId })
}
export function updateTaxSettings(value: TaxSettings, ownerId: string) {
  return callFn<{ ok: boolean; value: TaxSettings }>('settings-update', { key: 'tax', value, owner_id: ownerId })
}

export function cancelSale(saleId: string, employeeId: string) {
  return callFn<{ ok: boolean }>('sale-cancel', { sale_id: saleId, employee_id: employeeId })
}

export async function saleStatus(saleId: string) {
  const { data } = await supabase.from('sales').select('status, shopify_order_name, total_cents, paid_at').eq('id', saleId).maybeSingle()
  return data as { status: string; shopify_order_name: string | null; total_cents: number; paid_at: string | null } | null
}

export function voidSale(saleId: string, ownerId: string, reason: string) {
  return callFn<{ ok: boolean }>('sale-void', { sale_id: saleId, owner_id: ownerId, reason })
}

export async function loadSales(limit = 50): Promise<SaleRow[]> {
  const { data, error } = await supabase.from('sales').select('id, sale_no, employee_id, approved_by, payment_method, total_cents, subtotal_cents, tax_cents, voided, voided_by, status, source, shopify_order_name, created_at, customer_name, trade_id, trades(trade_no, status), payments:sale_payments(method, amount_cents, status), lines:sale_lines(title, sku, price_cents, below_floor)').order('sale_no', { ascending: false }).limit(limit)
  if (error) throw error
  return data as unknown as SaleRow[]
}

export async function loadLast7(): Promise<{ payment_method: string; source: string; sales: number; total_cents: number }[]> {
  const { data } = await supabase.from('v_last7').select('*')
  return (data ?? []) as { payment_method: string; source: string; sales: number; total_cents: number }[]
}
export async function loadToday(): Promise<{ payment_method: string; source: string; sales: number; total_cents: number }[]> {
  const { data } = await supabase.from('v_today').select('*')
  return (data ?? []) as { payment_method: string; source: string; sales: number; total_cents: number }[]
}

// ---- trades
export function createTrade(t: TradeDraft, employeeId: string, approvedBy: string | null) {
  return callFn<{ trade_id: string; trade_no: number; sale_id: string; out_total_cents: number; in_total_cents: number; difference_cents: number; direction: string; settlement_method: string; status: string; stock: Record<string, string> | null }>('trade-create', {
    employee_id: employeeId, approved_by: approvedBy, settlement_method: t.method || 'none', crypto_coin: t.coin, crypto_network: t.network, customer_id: t.customerId || null, customer_name: t.customerName,
    out_lines: t.out.map((l) => ({ ...l.piece, price_cents: lineCents(l) })),
    in_lines: t.give.map((g) => ({ brand: g.brand.trim(), item: g.item.trim(), size: g.size.trim(), condition: g.cond, credit_cents: giveCents(g), whose_piece: g.whose, deal_note: g.note.trim() })),
  })
}
export function confirmTradePayment(tradeId: string, employeeId: string, extra: { zelle_sender?: string; crypto_tx?: string; crypto_coin?: string; crypto_network?: string; paypal_ref?: string }) {
  return callFn<{ ok: boolean; stock: Record<string, string>; intake_ids?: string[] }>('trade-confirm-payment', { trade_id: tradeId, employee_id: employeeId, ...extra })
}
export function cancelTrade(tradeId: string, employeeId: string) {
  return callFn<{ ok: boolean }>('trade-cancel', { trade_id: tradeId, employee_id: employeeId })
}
export function voidTrade(tradeId: string, ownerId: string, reason: string) {
  return callFn<{ ok: boolean; refund_order: string | null }>('trade-void', { trade_id: tradeId, owner_id: ownerId, reason })
}
export async function loadTrade(tradeId: string) {
  const { data, error } = await supabase.from('trades').select('*').eq('id', tradeId).maybeSingle()
  if (error) throw error
  const t = data as TradeRow | null
  if (!t) return null
  const [{ data: lines }, { data: give }, { data: intakes }] = await Promise.all([
    t.sale_id ? supabase.from('sale_lines').select('title, sku, price_cents, list_price_cents, below_floor, owner').eq('sale_id', t.sale_id).order('position') : Promise.resolve({ data: [] }),
    supabase.from('trade_in_lines').select('brand, item, size, condition, credit_cents, whose_piece').eq('trade_id', tradeId).order('position'),
    supabase.from('intakes').select('id, intake_no, brand, item, status').eq('trade_id', tradeId),
  ])
  return { ...t, lines: (lines ?? []) as { title: string; sku: string; price_cents: number; list_price_cents: number; below_floor: boolean; owner: string | null }[], give: (give ?? []) as { brand: string; item: string; size: string; condition: string; credit_cents: number; whose_piece: string }[], intakes: (intakes ?? []) as { id: string; intake_no: number | null; brand: string; item: string; status: string }[] }
}
export async function tradeStatus(tradeId: string) {
  const { data } = await supabase.from('trades').select('status, shopify_order_name, mismatch, mismatch_note, pos_trade_credit_cents, paid_at').eq('id', tradeId).maybeSingle()
  return data as { status: string; shopify_order_name: string | null; mismatch: boolean; mismatch_note: string; pos_trade_credit_cents: number | null; paid_at: string | null } | null
}
export async function loadTradeLimits(): Promise<TradeLimits> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'trade_limits').maybeSingle()
  const v = (data?.value ?? {}) as Partial<TradeLimits>
  return { staff_credit_limit_cents: v.staff_credit_limit_cents ?? 150000, trade_tender_name: v.trade_tender_name ?? 'Trade-in credit', unpriced_days: v.unpriced_days ?? 14 }
}
export async function loadOwnerKeys(): Promise<Record<string, string | null>> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'owner_keys').maybeSingle()
  return (data?.value ?? {}) as Record<string, string | null>
}
/** The latest recorded intakes, newest first, for Intake → Recent intakes (reprint a card that didn't print). */
export async function loadRecentIntakes(limit = 150): Promise<IntakeRow[]> {
  const { data, error } = await supabase.from('intakes').select(INTAKE_CARD_COLS).neq('status', 'pending').order('created_at', { ascending: false }).limit(limit)
  if (error) throw new Error('Could not load recent intakes: ' + error.message)
  return (data ?? []) as unknown as IntakeRow[]
}

export async function loadPendingIntakes(): Promise<PendingIntake[]> {
  const { data, error } = await supabase.from('intakes').select('id, trade_no, trade_id, brand, item, size, condition, credit_cents, cost_cents, whose_piece, counterparty_name, deal_note, created_at').eq('status', 'pending').order('created_at')
  if (error) throw error
  return (data ?? []) as PendingIntake[]
}
export async function loadTrades(limit = 50): Promise<TradeRow[]> {
  const { data, error } = await supabase.from('trades').select('*').order('trade_no', { ascending: false }).limit(limit)
  if (error) throw error
  return (data ?? []) as TradeRow[]
}

export interface OpenSale { id: string; sale_no: number; status: string; payment_method: string; total_cents: number; created_at: string; employee_id: string; customer: string }
export interface DayClose { id: string; day: string; closed_by: string; closed_at: string; float_cents: number; cash_sales_cents: number; cash_payouts_cents: number; expected_cents: number; counted_cents: number; over_short_cents: number; totals: Record<string, { sales: number; total_cents: number }>; open_sales: number; intakes: number; notes: string; checklist?: { id: string; title: string; employee_id: string; name: string; done_at: string }[]; recounted?: boolean; first_count_cents?: number | null }
export interface CloseTask { id: string; title: string; desc?: string }
export interface CloseTaskDone { task_id: string; employee_id: string; done_at: string }
export interface Punch { id: string; employee_id: string; name: string; day: string; clock_in: string; clock_out: string | null; out_by: string | null; out_reason: string; hours: number; on_clock: boolean }
export async function loadCloseData(): Promise<{ today: { payment_method: string; source: string; sales: number; total_cents: number }[]; open: OpenSale[]; intakes: number; cash_payouts_cents: number; payouts: { bought_cents: number; bought_pieces: number; cash: number; zelle: number; wire: number; crypto: number }; float_cents: number; closed: DayClose | null; tasks: CloseTask[]; done: CloseTaskDone[]; onClock: Punch[] }> {
  const day = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
  const [t, o, i, f, c, k, d, p] = await Promise.all([
    supabase.from('v_today').select('*'), supabase.from('v_today_open').select('*'), supabase.from('v_today_intakes').select('*').maybeSingle(),
    supabase.from('app_settings').select('value').eq('key', 'cash_float').maybeSingle(), supabase.from('day_closes').select('*').eq('day', day).maybeSingle(),
    supabase.from('app_settings').select('value').eq('key', 'close_tasks').maybeSingle(), supabase.from('close_tasks').select('task_id, employee_id, done_at').eq('day', day),
    loadOnClock(),
  ])
  return {
    tasks: ((k.data?.value ?? []) as CloseTask[]), done: (d.data ?? []) as CloseTaskDone[], onClock: p,
    today: (t.data ?? []) as { payment_method: string; source: string; sales: number; total_cents: number }[], open: (o.data ?? []) as OpenSale[],
    intakes: Number(i.data?.intakes ?? 0), cash_payouts_cents: Number(i.data?.cash_payouts_cents ?? 0),
    payouts: { bought_cents: Number(i.data?.bought_cents ?? i.data?.cash_payouts_cents ?? 0), bought_pieces: Number(i.data?.bought_pieces ?? 0), cash: Number(i.data?.cash_payouts_cents ?? 0), zelle: Number(i.data?.zelle_payouts_cents ?? 0), wire: Number(i.data?.wire_payouts_cents ?? 0), crypto: Number(i.data?.crypto_payouts_cents ?? 0) },
    float_cents: Number((f.data?.value as { cents?: number } | null)?.cents ?? 0), closed: (c.data as DayClose | null) ?? null,
  }
}
export function closeDay(input: { employee_id: string; float_cents: number; counted_cents: number; first_count_cents: number | null; recounted: boolean; notes: string; no_cash?: boolean; skip_count?: boolean }) {
  return callFn<DayClose>('day-close', input)
}
export function setCloseTask(employeeId: string, taskId: string, done: boolean) {
  return callFn<CloseTaskDone | { ok: boolean }>('day-close', { action: 'task', employee_id: employeeId, task_id: taskId, done })
}

// Time clock
const nyDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
/** Who is on the clock today (an open shift from an earlier day is a missed clock-out, not someone in the showroom). */
export async function loadOnClock(): Promise<Punch[]> {
  const { data, error } = await supabase.from('v_time_cards').select('*').eq('on_clock', true).eq('day', nyDate()).order('clock_in')
  if (error) throw error
  return (data ?? []) as Punch[]
}
export async function loadTimeCards(fromDay: string, employeeId?: string): Promise<Punch[]> {
  let q = supabase.from('v_time_cards').select('*').gte('day', fromDay).order('clock_in', { ascending: false }).limit(500)
  if (employeeId) q = q.eq('employee_id', employeeId)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as Punch[]
}
export function clockIn(employeeId: string) {
  return callFn<{ punch: Punch; already?: boolean }>('time-clock', { action: 'in', employee_id: employeeId })
}
export function clockOut(employeeId: string, forId?: string) {
  return callFn<{ punch: Punch }>('time-clock', { action: 'out', employee_id: employeeId, for_id: forId })
}

/** Sign this device in with an employee ID (no pairing): the server hands back the store session. */
export async function idSignIn(employeeId: string): Promise<Employee> {
  let device = ''
  try { device = localStorage.getItem('starchive.device') ?? ''; if (!device) { device = crypto.randomUUID(); localStorage.setItem('starchive.device', device) } } catch { /* private mode */ }
  const r = await callFn<{ access_token: string; refresh_token: string; employee: Employee }>('id-sign-in', { employee_id: employeeId, device })
  const { error } = await supabase.auth.setSession({ access_token: r.access_token, refresh_token: r.refresh_token })
  if (error) throw new Error(error.message)
  return r.employee
}
export function reopenDay(employeeId: string) {
  return callFn<{ ok: boolean }>('day-close', { action: 'reopen', employee_id: employeeId })
}

export function syncConsignors() {
  return callFn<{ added: number; updated: number; total: number }>('consignor-sync', {})
}

// ── Website Inbox (Sell / Consign submissions and Contact messages from starchive.la) ──
export type WebSubStatus = 'new' | 'reviewing' | 'reached_out' | 'offer_made' | 'accepted' | 'passed'
/** Submissions still waiting on a decision: they count on the red badge until Offer made, Accepted or Passed. */
export const WEB_WAITING: WebSubStatus[] = ['new', 'reviewing', 'reached_out']
export interface WebPiece { id: string; position: number; brand: string; category: string; item: string; size: string; asking_cents: number | null; condition: 'nwt' | 'excellent' | 'good' | 'fair'; has_receipt: boolean; has_box: boolean; has_extras: boolean; flaws: string; intake_started_at: string | null; photos: string[] }
export interface WebSubmission { kind: 's'; id: string; ref_no: number; submitted_at: string; status: WebSubStatus; name: string; email: string; phone: string; instagram: string; city: string; contact_pref: string; intent: 'sell' | 'consign' | 'either'; location: 'nyc' | 'ship'; notes: string; accepted_as: 'buy' | 'consign' | null; assigned_to: string | null; staff_notes: string; updated_by: string | null; pieces: WebPiece[] }
export interface WebMessage { kind: 'm'; id: string; created_at: string; status: 'new' | 'replied' | 'closed'; topic: 'product' | 'order' | 'press' | 'other' | 'vip'; name: string; email: string; phone: string; instagram: string; order_no: string; product_ref: string; message: string; staff_notes: string; updated_by: string | null }

export async function loadWebInbox(): Promise<{ subs: WebSubmission[]; msgs: WebMessage[] }> {
  const since = new Date(Date.now() - 180 * 86400_000).toISOString()
  const [s, p, ph, m] = await Promise.all([
    supabase.from('web_submissions').select('id, ref_no, submitted_at, status, name, email, phone, instagram, city, contact_pref, intent, location, notes, accepted_as, assigned_to, staff_notes, updated_by').neq('status', 'draft').gte('submitted_at', since).order('submitted_at', { ascending: false }),
    supabase.from('web_submission_pieces').select('id, submission_id, position, brand, category, item, size, asking_cents, condition, has_receipt, has_box, has_extras, flaws, intake_started_at').order('position'),
    supabase.from('web_submission_photos').select('piece_id, position, path').order('position'),
    supabase.from('web_messages').select('id, created_at, status, topic, name, email, phone, instagram, order_no, product_ref, message, staff_notes, updated_by').gte('created_at', since).order('created_at', { ascending: false }),
  ])
  if (s.error) throw s.error
  if (m.error) throw m.error
  const photos = new Map<string, string[]>()
  for (const x of (ph.data ?? []) as { piece_id: string; path: string }[]) photos.set(x.piece_id, [...(photos.get(x.piece_id) ?? []), x.path])
  const pieces = new Map<string, WebPiece[]>()
  for (const x of (p.data ?? []) as (WebPiece & { submission_id: string })[]) pieces.set(x.submission_id, [...(pieces.get(x.submission_id) ?? []), { ...x, photos: photos.get(x.id) ?? [] }])
  return {
    subs: ((s.data ?? []) as Omit<WebSubmission, 'kind' | 'pieces'>[]).map((x) => ({ ...x, kind: 's' as const, pieces: pieces.get(x.id) ?? [] })),
    msgs: ((m.data ?? []) as Omit<WebMessage, 'kind'>[]).map((x) => ({ ...x, kind: 'm' as const })),
  }
}

export async function webPhotoUrls(paths: string[]): Promise<Record<string, string>> {
  if (!paths.length) return {}
  const { data, error } = await supabase.storage.from('web-submissions').createSignedUrls(paths, 3600)
  if (error) throw error
  const out: Record<string, string> = {}
  for (const d of data ?? []) if (d.path && d.signedUrl) out[d.path] = d.signedUrl
  return out
}

export async function countWebNew(): Promise<number> {
  const [a, b] = await Promise.all([
    // a Sell / Consign submission keeps the red count until it is marked Offer made, Accepted or Passed (Tim, 30 Sep)
    supabase.from('web_submissions').select('id', { count: 'exact', head: true }).in('status', WEB_WAITING),
    supabase.from('web_messages').select('id', { count: 'exact', head: true }).eq('status', 'new'),
  ])
  return (a.count ?? 0) + (b.count ?? 0)
}

export function updateWebItem(kind: 's' | 'm', id: string, patch: Record<string, unknown>, employeeId: string) {
  return callFn<Record<string, unknown>>('web-inbox', { kind, id, patch, employee_id: employeeId })
}
export function markWebPieceIntake(pieceId: string, employeeId: string) {
  return callFn<{ ok: true }>('web-inbox', { kind: 'piece', id: pieceId, employee_id: employeeId })
}
